import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Box, FormHelperText, FormLabel, Skeleton } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { RichTextEditor } from "@foodpilot/foods";

type ConsentRichTextFieldProps = {
    disabled?: boolean;
    editorKey: string;
    error?: boolean;
    helperText?: string;
    id: string;
    label: string;
    loading?: boolean;
    loadingLabel?: string;
    minHeight?: number;
    onChange: (value: string) => void;
    placeholder?: string;
    value?: string | null;
};

export const ConsentRichTextField = ({
    disabled = false,
    editorKey,
    error = false,
    helperText,
    id,
    label,
    loading = false,
    loadingLabel = "Loading rich text content",
    minHeight = 160,
    onChange,
    placeholder,
    value,
}: ConsentRichTextFieldProps) => {
    const theme = useTheme();
    const content = value ?? "";
    const [editorRevision, setEditorRevision] = useState(0);
    const lastEditorKeyRef = useRef(editorKey);
    const lastEditorValueRef = useRef(content);
    const customGrey = theme.custom?.grey ?? {};
    const mutedColor = customGrey[2000] ?? theme.palette.text.secondary;
    const borderColor = error ? theme.palette.error.main : (customGrey[500] ?? theme.palette.divider);
    const richTextEditorKey = `${editorKey}-${editorRevision}`;

    useLayoutEffect(() => {
        if (lastEditorKeyRef.current !== editorKey) {
            lastEditorKeyRef.current = editorKey;
            lastEditorValueRef.current = content;

            return;
        }

        if (loading) {
            lastEditorValueRef.current = content;

            return;
        }

        if (lastEditorValueRef.current !== content) {
            lastEditorValueRef.current = content;
            setEditorRevision((currentRevision) => currentRevision + 1);
        }
    }, [content, editorKey, loading]);

    const handleEditorChange = useCallback(
        (nextValue: string) => {
            if (disabled) {
                return;
            }

            lastEditorValueRef.current = nextValue;
            onChange(nextValue);
        },
        [disabled, onChange],
    );

    return (
        <Box>
            <FormLabel
                htmlFor={id}
                error={error}
                sx={{
                    color: mutedColor,
                    display: "block",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    mb: 0.75,
                }}
            >
                {label}
            </FormLabel>
            <Box
                aria-disabled={disabled}
                aria-invalid={error}
                sx={{
                    "& > div": {
                        border: `1px solid ${borderColor}`,
                        borderRadius: theme.spacing(2),
                        transition: "border-color 160ms ease, box-shadow 160ms ease",
                    },
                    "& > div:focus-within": {
                        borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
                        boxShadow: `0 0 0 3px ${alpha(
                            error ? theme.palette.error.main : theme.palette.primary.main,
                            0.12,
                        )}`,
                    },
                    "& .ProseMirror": {
                        minHeight,
                        pointerEvents: disabled ? "none" : "auto",
                    },
                    opacity: disabled ? 0.72 : 1,
                    "& img:not(.inline-svg-icon)": {
                        borderRadius: theme.spacing(1),
                        display: "block",
                        height: "auto",
                        maxWidth: "100%",
                    },
                }}
            >
                {loading ?
                    <Box
                        aria-label={loadingLabel}
                        aria-live="polite"
                        role="status"
                        sx={{
                            border: `1px solid ${borderColor}`,
                            borderRadius: theme.spacing(2),
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                alignItems: "center",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                display: "flex",
                                gap: 1,
                                minHeight: 48,
                                px: 2,
                            }}
                        >
                            <Skeleton height={28} variant="rounded" width={150} />
                            <Skeleton height={28} variant="rounded" width={150} />
                            <Skeleton height={28} variant="rounded" width={96} />
                            <Skeleton height={28} variant="circular" width={28} />
                            <Skeleton height={28} variant="circular" width={28} />
                        </Box>
                        <Box sx={{ minHeight, p: 2 }}>
                            <Skeleton height={24} width="70%" />
                            <Skeleton height={24} width="48%" />
                        </Box>
                    </Box>
                :   <RichTextEditor
                        allowBase64Images
                        key={richTextEditorKey}
                        initialContent={content}
                        onChange={handleEditorChange}
                        placeholder={placeholder}
                        value={content}
                    />
                }
            </Box>
            {helperText && (
                <FormHelperText error={error} sx={{ ml: 0 }}>
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};
